import { existsSync, readFileSync, statSync } from "fs";
import { extname, isAbsolute, relative, resolve } from "path";
import { brotliCompressSync, gzipSync } from "zlib";

import type { Handler } from "hono";

export const DASHBOARD_MIME_MAP: Record<string, string> = {
  ".html": "text/html",
  ".js": "application/javascript",
  ".mjs": "application/javascript",
  ".css": "text/css",
  ".json": "application/json",
  ".png": "image/png",
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".svg": "image/svg+xml",
  ".ico": "image/x-icon",
  ".woff": "font/woff",
  ".woff2": "font/woff2",
  ".ttf": "font/ttf",
  ".map": "application/json",
  ".txt": "text/plain",
  ".webmanifest": "application/manifest+json",
  ".xml": "application/xml",
  ".wasm": "application/wasm",
};

export const CLOUD_DASHBOARD_EXPLICIT_ROUTES = ["/", "/login", "/signup"] as const;

export const CLOUD_DASHBOARD_SYSTEM_PATH_PREFIXES = [
  "/api/",
  "/health",
  "/ws",
  "/_gencow/",
  "/_admin",
  "/_dashboard",
  "/_proxy/",
  "/internal/",
  "/platform/",
] as const;

type DashboardEncoding = "br" | "gzip";
type CachedDashboardBody = { body: Buffer; createdAt: number };

const COMPRESSIBLE_DASHBOARD_EXTENSIONS = new Set([
  ".css",
  ".html",
  ".js",
  ".json",
  ".mjs",
  ".svg",
  ".txt",
  ".webmanifest",
  ".xml",
]);
const STATIC_ASSET_PATH_PREFIXES = ["/assets/"] as const;
const STATIC_ASSET_FILE_NAMES = new Set([
  "/apple-touch-icon.png",
  "/favicon.ico",
  "/manifest.json",
  "/robots.txt",
  "/site.webmanifest",
]);
const encodedDashboardBodyCache = new Map<string, CachedDashboardBody>();
const encodedDashboardBodyCacheTtlMs = 10 * 60 * 1000;
const encodedDashboardBodyCacheMaxEntries = 120;

type HeaderReadableContext = {
  req?: {
    header?: (name: string) => string | undefined;
    headers?: { get(name: string): string | null };
  };
};

function acceptsEncoding(value: string, encoding: DashboardEncoding): boolean {
  return value
    .toLowerCase()
    .split(",")
    .map((part) => part.trim())
    .some((token) => {
      if (!token.startsWith(encoding)) return false;
      const q = token.match(/;\s*q=([0-9.]+)/)?.[1];
      return q === undefined || Number(q) > 0;
    });
}

function preferredDashboardEncoding(acceptEncoding: string, ext: string): DashboardEncoding | null {
  if (!COMPRESSIBLE_DASHBOARD_EXTENSIONS.has(ext.toLowerCase())) return null;
  if (acceptsEncoding(acceptEncoding, "br")) return "br";
  if (acceptsEncoding(acceptEncoding, "gzip")) return "gzip";
  return null;
}

function toBuffer(content: string | Buffer): Buffer {
  return Buffer.isBuffer(content) ? content : Buffer.from(content);
}

function pruneEncodedDashboardBodyCache(now = Date.now()): void {
  for (const [key, value] of encodedDashboardBodyCache) {
    if (now - value.createdAt > encodedDashboardBodyCacheTtlMs) encodedDashboardBodyCache.delete(key);
  }
  while (encodedDashboardBodyCache.size > encodedDashboardBodyCacheMaxEntries) {
    const oldest = encodedDashboardBodyCache.keys().next().value as string | undefined;
    if (!oldest) break;
    encodedDashboardBodyCache.delete(oldest);
  }
}

function dashboardCacheKey(filePath: string, bodySize: number, encoding: DashboardEncoding): string {
  let mtimeMs = 0;
  try {
    mtimeMs = statSync(filePath).mtimeMs;
  } catch {
    mtimeMs = 0;
  }
  return `${filePath}:${mtimeMs}:${bodySize}:${encoding}`;
}

function encodeDashboardBody(
  body: Buffer,
  encoding: DashboardEncoding | null,
  cacheKey?: string,
): { body: Buffer; encoding: DashboardEncoding | null } {
  if (!encoding || body.byteLength === 0) return { body, encoding: null };
  pruneEncodedDashboardBodyCache();
  if (cacheKey) {
    const cached = encodedDashboardBodyCache.get(cacheKey);
    if (cached) return { body: cached.body, encoding };
  }
  const encoded = encoding === "br" ? brotliCompressSync(body) : gzipSync(body);
  if (encoded.byteLength >= body.byteLength) return { body, encoding: null };
  if (cacheKey) encodedDashboardBodyCache.set(cacheKey, { body: encoded, createdAt: Date.now() });
  return { body: encoded, encoding };
}

function requestHeader(c: HeaderReadableContext, name: string): string {
  const direct = c.req?.header?.(name);
  if (typeof direct === "string") return direct;
  const fromHeaders = c.req?.headers?.get?.(name);
  return typeof fromHeaders === "string" ? fromHeaders : "";
}

function normalizeRequestPath(path: string): string {
  if (!path) return "/";
  return path.startsWith("/") ? path : `/${path}`;
}

function resolveDashboardFilePath(root: string, requestPath: string): string | null {
  const trimmedPath = normalizeRequestPath(requestPath).replace(/^\/+/, "") || "index.html";
  let decodedPath: string;
  try {
    decodedPath = decodeURIComponent(trimmedPath);
  } catch {
    return null;
  }
  if (decodedPath.includes("\0") || decodedPath.split(/[\\/]+/).includes("..")) {
    return null;
  }
  const filePath = resolve(root, decodedPath);
  const relativePath = relative(root, filePath);
  if (!relativePath || relativePath.startsWith("..") || isAbsolute(relativePath)) {
    return null;
  }
  return filePath;
}

export function shouldServeCloudDashboardSpaFallback(
  path: string,
  prefixes: readonly string[] = CLOUD_DASHBOARD_SYSTEM_PATH_PREFIXES,
): boolean {
  const normalizedPath = normalizeRequestPath(path);
  return !prefixes.some((prefix) => normalizedPath.startsWith(prefix));
}

export function isCloudDashboardStaticAssetPath(
  path: string,
  mimeMap: Record<string, string> = DASHBOARD_MIME_MAP,
): boolean {
  const normalizedPath = normalizeRequestPath(path);
  if (STATIC_ASSET_PATH_PREFIXES.some((prefix) => normalizedPath.startsWith(prefix))) return true;
  if (STATIC_ASSET_FILE_NAMES.has(normalizedPath)) return true;
  const ext = extname(normalizedPath).toLowerCase();
  return Boolean(ext && mimeMap[ext]);
}

export function findCloudDashboardDir(
  searchPaths: string[],
  fileExists: (path: string) => boolean = existsSync,
): string | undefined {
  return searchPaths.find((candidate) => fileExists(candidate));
}

function buildDashboardResponse(params: {
  body: Buffer;
  contentType: string;
  cacheControl: string;
  method: string;
  encoding: DashboardEncoding | null;
  cacheKey?: string;
}): Response {
  const encoded = encodeDashboardBody(params.body, params.encoding, params.cacheKey);
  const headers = new Headers({
    "Content-Type": params.contentType,
    "Cache-Control": params.cacheControl,
    "Content-Length": String(encoded.body.byteLength),
  });
  if (encoded.encoding) {
    headers.set("Content-Encoding", encoded.encoding);
    headers.set("Vary", "Accept-Encoding");
  }
  const body = new Blob([
    encoded.body.buffer.slice(
      encoded.body.byteOffset,
      encoded.body.byteOffset + encoded.body.byteLength,
    ) as ArrayBuffer,
  ]);
  return new Response(params.method === "HEAD" ? null : body, { headers });
}

export function createCloudDashboardHandler(params: {
  cloudDashboardDir: string;
  indexHtml: string;
  fileExists?: (path: string) => boolean;
  isFile?: (path: string) => boolean;
  readFile?: (path: string) => string | Buffer;
  mimeMap?: Record<string, string>;
  canServeSpaFallback?: (path: string) => boolean;
}): Handler {
  const fileExists = params.fileExists ?? existsSync;
  const isFile = params.isFile ?? ((path: string) => statSync(path).isFile());
  const readFile = params.readFile ?? readFileSync;
  const mimeMap = params.mimeMap ?? DASHBOARD_MIME_MAP;
  const canServeSpaFallback = params.canServeSpaFallback ?? shouldServeCloudDashboardSpaFallback;

  return (c) => {
    const reqPath = normalizeRequestPath(c.req.path);
    const method = String(c.req.method ?? "").toUpperCase();
    if (!canServeSpaFallback(reqPath)) {
      return c.notFound();
    }

    const filePath = resolveDashboardFilePath(params.cloudDashboardDir, reqPath);
    if (!filePath) {
      return c.notFound();
    }

    if (reqPath !== "/" && fileExists(filePath) && isFile(filePath)) {
      const ext = extname(filePath).toLowerCase();
      const body = toBuffer(readFile(filePath));
      const encoding = preferredDashboardEncoding(requestHeader(c, "Accept-Encoding"), ext);
      return buildDashboardResponse({
        body,
        contentType: mimeMap[ext] || "application/octet-stream",
        cacheControl: ext === ".html" ? "no-cache" : "public, max-age=31536000, immutable",
        method,
        encoding,
        cacheKey: encoding ? dashboardCacheKey(filePath, body.byteLength, encoding) : undefined,
      });
    }

    if (isCloudDashboardStaticAssetPath(reqPath, mimeMap)) {
      return c.notFound();
    }

    return buildDashboardResponse({
      body: Buffer.from(params.indexHtml),
      contentType: "text/html",
      cacheControl: "no-cache",
      method,
      encoding: preferredDashboardEncoding(requestHeader(c, "Accept-Encoding"), ".html"),
    });
  };
}

export function registerCloudDashboardRoutes(params: {
  app: { get: (path: string, handler: Handler) => unknown };
  handler: Handler;
}): void {
  for (const route of CLOUD_DASHBOARD_EXPLICIT_ROUTES) {
    params.app.get(route, params.handler);
  }
  params.app.get("/*", params.handler);
}
